import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, DropdownButton, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { formatDate, hasAccessRight } from '../../../utils/Functions';
import { ErrorToast } from '../../../utils/toasters';
import { Offer } from '../../../model/offer/Offer';
import DropdownItem from 'react-bootstrap/DropdownItem';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { UPDATED_OFFER } from '../../../enum/Messages';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { OfferService } from '../../offer/OfferService';
import { OfferFilter } from '../../offer/components/OfferFilter';
import { AddAdditionalItemModal } from '../../offer/components/AddAdditionalModal';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

const INCOMING_INVOICES_COLUMNS: ITableColumn[] = [
    { label: 'Date' },
    { label: 'Offer ID' },
    { label: 'Company' },
    { label: 'Influencer' },
    { label: 'Campaign' },
    { label: 'Status' },
    { label: 'Type' },
    { label: 'Price' },
    { label: 'Agency?' },
    { label: 'Current?' },
    { label: 'Action' },
];

export function IncomingInvoices() {
    const navigate = useNavigate();
    const [dataLoading, setDataLoading] = useState(true);
    const [offers, setOffers] = useState<Offer[]>([]);
    const requestIdRef = React.useRef(0);
    const [filters, setFilters] = useState({
        page: 1,
        perPage: 15,
        id: '',
        date: '',
        influencerId: '',
        campaignId: '',
        status: '',
        totalPages: 0,
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);

    useEffect(() => {
        fetchOffers(filters);
    }, [filters.campaignId, filters.influencerId]);

    const fetchOffers = (params: typeof filters, pageOverride?: number) => {
        const requestId = ++requestIdRef.current;
        setDataLoading(true);
        const page = pageOverride ?? params.page;

        OfferService.getOffers({ ...params, page })
            .then(({ data, page: responsePage, count }) => {
                if (requestId !== requestIdRef.current) return;

                const totalPages = Math.ceil(count / params.perPage);

                if (totalPages > 0 && page > totalPages) {
                    fetchOffers({ ...params, page: 1 });
                    return;
                }

                setOffers(data);
                setFilters(() => ({
                    ...params,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(err => {
                if (requestId === requestIdRef.current) ErrorToast(err);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setDataLoading(false);
            });
    };

    const handlePageChange = (page: number) => {
        fetchOffers(filters, page);
    };

    const getOfferType = (isAddonsOnly?: boolean, isLongTerm?: boolean) =>
        isAddonsOnly ? 'Add-ons' : isLongTerm ? 'Spending' : 'Normal';

    const renderStatusIcon = (condition?: boolean) =>
        condition ? (
            <span className="word-break text-primary">
                <FaCheck />
            </span>
        ) : (
            <span className="word-break text-danger">
                <FaTimes />
            </span>
        );

    const renderWithTooltip = (text?: string) =>
        text ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="info-tooltip">{text}</Tooltip>}>
                <span className="ml-1">
                    <FiInfo size={14} className="cursor-pointer" />
                </span>
            </OverlayTrigger>
        ) : null;

    const handleAddAdditional = async (data: any) => {
        if (selectedOffer) {
            OfferService.addAdditionalItem(selectedOffer.id, { ...data })
                .then(() => {
                    toast(UPDATED_OFFER);
                })
                .catch(err => ErrorToast(err));
        }
    };

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') fetchOffers(filters);
                }}
            >
                <OfferFilter handleState={fetchOffers} setState={setFilters} offersFilter={filters} />
            </Card>

            <AddAdditionalItemModal
                offer={selectedOffer}
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={handleAddAdditional}
            />

            <TableLayout
                pagination={filters}
                listLength={offers.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'offers'}
                skeletonColumns={INCOMING_INVOICES_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {INCOMING_INVOICES_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {offers.map(offer => (
                            <tr key={offer.id}>
                                <td>{formatDate(offer.createdAt)}</td>
                                <td>{offer.id}</td>
                                <td>
                                    <ImpersonateEye id={offer?.campaign?.company?.createdBy} />
                                    {offer?.campaign?.company?.displayName}
                                </td>
                                <td>
                                    <ImpersonateEye id={offer?.user?.id ?? 0} />
                                    {offer?.user?.displayName}
                                    <br />
                                    {offer?.user?.firstName ?? '-'} {offer?.user?.lastName}
                                </td>
                                <td>
                                    {offer?.campaign?.id}
                                    {renderWithTooltip(offer?.campaign?.title)}
                                </td>
                                <td>
                                    <span className={`status ${offer?.status}`}>{offer?.status}</span>
                                </td>
                                <td>{getOfferType(offer.isAddonsOnly, offer.isLongTerm)}</td>
                                <td>{priceSplitter(offer?.price)} €</td>
                                <td>
                                    {offer?.user?.agencyCompany?.id ? (
                                        <>
                                            {offer?.user?.agencyCompany?.id}
                                            {renderWithTooltip(offer?.user?.agencyCompany?.displayName)}
                                        </>
                                    ) : (
                                        renderStatusIcon(false)
                                    )}
                                </td>
                                <td>{renderStatusIcon(offer?.isCurrent)}</td>
                                <td>
                                    <DropdownButton title="Actions">
                                        <DropdownItem
                                            onClick={() =>
                                                navigate(`/offers/edit/${offer.id}`, {
                                                    state: { id: offer.id, offer: offer },
                                                })
                                            }
                                        >
                                            Edit
                                        </DropdownItem>
                                        {hasAccessRight(AdminAclEnum.ADD_ADDITIONAL_ITEM) &&
                                            offer.isCurrent &&
                                            offer.isLongTerm === false && (
                                                <DropdownItem
                                                    onClick={() => {
                                                        setSelectedOffer(offer);
                                                        setShowAddModal(true);
                                                    }}
                                                >
                                                    Add additional item
                                                </DropdownItem>
                                            )}
                                    </DropdownButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
